import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPayments } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/payments")({
  component: PaymentsPage,
});

const COLOR: Record<string, string> = {
  succeeded: "text-emerald-300",
  pending: "text-amber-300",
  failed: "text-rose-300",
  refunded: "text-sky-300",
};

function PaymentsPage() {
  const fn = useServerFn(listPayments);
  const { data = [], isLoading } = useQuery({ queryKey: ["payments"], queryFn: () => fn() });

  const total = data.filter((p) => p.status === "succeeded").reduce((s, p) => s + p.amount_cents, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl">Payments</h1>
        <p className="mt-1 text-sm text-muted-foreground">Deposits captured for reservations.</p>
      </div>
      <div className="rounded-2xl border border-border bg-card/40 p-5">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Total succeeded</div>
        <div className="mt-1 font-display text-3xl">€{(total / 100).toFixed(2)}</div>
      </div>
      <div className="rounded-2xl border border-border bg-card/30">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : data.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No payments yet.</div>
        ) : (
          <ul className="divide-y divide-border">
            {data.map((p) => {
              const r = (p as { reservations?: { reference?: string; booking_type?: string; starts_at?: string; guests?: { full_name?: string; phone?: string } } }).reservations;
              return (
                <li key={p.id} className="grid items-center gap-3 px-5 py-3 md:grid-cols-[1fr_180px_120px_100px]">
                  <div>
                    <div className="text-sm font-semibold">{r?.guests?.full_name ?? "Guest"} <span className="text-xs text-muted-foreground">· {r?.reference}</span></div>
                    <div className="text-xs text-muted-foreground">{r?.booking_type?.replace(/_/g, " ")} {r?.starts_at && `· ${new Date(r.starts_at).toLocaleString("en-IE")}`}</div>
                  </div>
                  <span className="text-xs text-muted-foreground">{p.stripe_payment_intent ?? "—"}</span>
                  <span className="font-mono text-sm">€{(p.amount_cents / 100).toFixed(2)} {p.currency.toUpperCase()}</span>
                  <span className={`text-xs uppercase tracking-wider ${COLOR[p.status] ?? ""}`}>{p.status}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
